import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

const enhanceSnackbarChildren = (children: React.ReactNode) => {
  if (!React.isValidElement(children)) {
    return children;
  }

  const existingClassName = (children.props as { className?: string }).className;

  return React.cloneElement(children, {
    className: cn(
      'bg-[var(--snackbar-surface-background)] text-[var(--snackbar-surface-foreground)]',
      'border-[var(--snackbar-surface-border)] shadow-hard backdrop-blur-md',
      existingClassName
    ),
  });
};

/* ─── Snackbar ───────────────────────────── */
export interface SnackbarProps {
  open?: boolean;
  message?: React.ReactNode;
  autoHideDuration?: number | null;
  onClose?: (event: React.SyntheticEvent | Event | null, reason?: string) => void;
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
  action?: React.ReactNode;
  children?: React.ReactNode;
  TransitionComponent?: React.ElementType;
  ContentProps?: Record<string, unknown>;
  key?: React.Key;
  className?: string;
  style?: React.CSSProperties;
}

const Snackbar: React.FC<SnackbarProps> = ({
  open = false,
  message,
  autoHideDuration = 6000,
  onClose,
  anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
  action,
  children,
  className,
  style,
}) => {
  React.useEffect(() => {
    if (open && autoHideDuration) {
      const t = setTimeout(() => onClose?.(null, 'timeout'), autoHideDuration);
      return () => clearTimeout(t);
    }
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const posClass = cn(
    'fixed z-[9999]',
    anchorOrigin.vertical === 'top' ? 'top-4' : null,
    anchorOrigin.horizontal === 'left' ? 'left-4' :
    anchorOrigin.horizontal === 'right' ? 'right-4' :
    'left-1/2 -translate-x-1/2'
  );

  const positionStyle: React.CSSProperties = anchorOrigin.vertical === 'bottom'
    ? { bottom: 'calc((var(--mobile-nav-total-offset-px, 0) * 1px) + env(safe-area-inset-bottom, 0px) + 16px)' }
    : {};

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(posClass, 'animate-slide-up', className)}
      style={{ ...positionStyle, ...style }}
    >
      {children ? enhanceSnackbarChildren(children) : (
        <div className="flex items-center gap-3 px-4 py-3 bg-[var(--snackbar-surface-background)] text-[var(--snackbar-surface-foreground)] border border-[var(--snackbar-surface-border)] rounded-[var(--radius-ui)] shadow-hard backdrop-blur-md min-w-[280px] max-w-[400px]">
          <span className="flex-1 text-sm font-sans">{message}</span>
          {action && <div className="shrink-0">{action}</div>}
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => onClose?.(e, 'closeButton')}
            className="shrink-0 rounded p-1 opacity-70 hover:opacity-100 focus-visible:ring-2 ring-current outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export { Snackbar };
